import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, ArrowRight, X, Calculator, Package, Factory, FlagBanner, Copy } from "@phosphor-icons/react";

const STEPS = [
  { key: "planned",     label: "Plan",      icon: Calculator },
  { key: "reserved",    label: "Reserve",   icon: Package },
  { key: "in_progress", label: "Issue",     icon: Factory },
  { key: "completed",   label: "Complete",  icon: FlagBanner },
];

const STATUS_TONE = {
  planned: "bg-slate-100 text-slate-700",
  reserved: "bg-amber-50 text-amber-700",
  in_progress: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

export default function ProductionRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [completeForm, setCompleteForm] = useState({ actual_yield: "", fg_batch_no: "", fg_expiry_date: "", notes: "" });
  const [showCompleteForm, setShowCompleteForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/production-runs/${id}`);
      setRun(data);
      setCompleteForm((f) => ({ ...f, actual_yield: data.batch_size }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run not found");
      navigate("/inventory/production-runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const act = async (action, body) => {
    setActing(true);
    try {
      const { data } = await api.post(`/inventory/production-runs/${id}/${action}`, body || {});
      setRun(data);
      toast.success(`Run ${action} ✓`);
      if (action === "complete") setShowCompleteForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActing(false);
    }
  };

  if (loading || !run) return <div className="text-sm text-slate-500">Loading run…</div>;

  const stepIdx = run.status === "cancelled" ? -1 : STEPS.findIndex((s) => s.key === run.status);
  const grouped = run.materials.reduce((acc, m) => {
    const key = m.section || "INGREDIENTS";
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="run-detail-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">Production Run · {run.code}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">{run.recipe_name}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Planned batch <span className="font-medium text-slate-700">{run.batch_size} kg</span> · created {new Date(run.created_at).toLocaleString()} by {run.created_by_name}
          </p>
        </div>
        <span className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-2 rounded ${STATUS_TONE[run.status]}`} data-testid="run-status">
          {run.status.replace("_", " ")}
        </span>
      </div>

      {/* Stepper */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => {
            const done = i <= stepIdx;
            const current = i === stepIdx;
            const Icon = s.icon;
            return (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center transition ${done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"} ${current && run.status !== "completed" ? "ring-4 ring-emerald-100" : ""}`}>
                    {done && !current ? <CheckCircle size={20} weight="bold" /> : <Icon size={20} weight={current ? "fill" : "bold"} />}
                  </div>
                  <div className={`text-xs font-medium mt-2 ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${i < stepIdx ? "bg-emerald-600" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Materials" value={run.materials.length} />
        <Tile label="Planned Cost" value={`₹${Number(run.total_material_cost).toLocaleString("en-IN")}`} />
        <Tile label="Actual Cost" value={run.actual_material_cost ? `₹${Number(run.actual_material_cost).toLocaleString("en-IN")}` : "—"} tone={run.actual_material_cost ? "emerald" : "slate"} />
        <Tile
          label="Yield Variance"
          value={run.actual_yield > 0 ? `${run.actual_yield} kg (${run.yield_variance_pct > 0 ? "+" : ""}${run.yield_variance_pct}%)` : "—"}
          tone={run.actual_yield > 0 ? (run.yield_variance_pct < -2 ? "rose" : "emerald") : "slate"}
        />
      </div>

      {/* Action panel */}
      {run.status === "planned" && (
        <ActionPanel
          title="Step 2 · Reserve materials"
          desc="Snapshot current stock against the recipe BOM. We'll block if there's a shortage."
          ctaLabel={`Reserve ${run.batch_size} kg of ${run.recipe_name}`}
          tone={run.total_shortage_at_plan > 0 ? "amber" : "emerald"}
          disabled={acting || run.total_shortage_at_plan > 0}
          warning={run.total_shortage_at_plan > 0 ? `Cannot reserve — short ${run.total_shortage_at_plan} kg of ingredients. Restock first.` : null}
          onClick={() => act("reserve")}
          onCancel={() => act("cancel").then(() => navigate("/inventory/production-runs"))}
          testId="action-reserve"
        />
      )}
      {run.status === "reserved" && (
        <ActionPanel
          title="Step 3 · Issue to production"
          desc="This will deduct the required quantities from stock via FIFO/FEFO and start the run. Can't be undone."
          ctaLabel="Issue Materials & Start Production"
          tone="sky"
          disabled={acting}
          warning="Issuing locks the consumption against this run. Make sure operators are ready."
          onClick={() => act("issue")}
          onCancel={() => act("cancel").then(() => navigate("/inventory/production-runs"))}
          testId="action-issue"
        />
      )}
      {run.status === "in_progress" && (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="font-heading text-base font-bold text-slate-900">Step 4 · Record finished goods</div>
          <div className="text-xs text-slate-500 mt-1">Capture the actual yield, FG batch, and expiry. We&apos;ll compute variance and write FG stock-in.</div>
          {!showCompleteForm && (
            <button
              onClick={() => setShowCompleteForm(true)}
              className="mt-4 h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              data-testid="open-complete-form"
            >
              Complete Run
            </button>
          )}
          {showCompleteForm && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                act("complete", { ...completeForm, actual_yield: Number(completeForm.actual_yield) });
              }}
              className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1">Actual yield (kg) *</span>
                <input required type="number" step="0.001" min="0.001" value={completeForm.actual_yield} onChange={(e) => setCompleteForm((f) => ({ ...f, actual_yield: e.target.value }))} className={inp} data-testid="complete-yield" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1">FG batch number</span>
                <input value={completeForm.fg_batch_no} onChange={(e) => setCompleteForm((f) => ({ ...f, fg_batch_no: e.target.value }))} className={inp} data-testid="complete-batch" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1">FG expiry date</span>
                <input type="date" value={completeForm.fg_expiry_date} onChange={(e) => setCompleteForm((f) => ({ ...f, fg_expiry_date: e.target.value }))} className={inp} data-testid="complete-expiry" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1">Notes</span>
                <input value={completeForm.notes} onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))} className={inp} />
              </label>
              <div className="col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setShowCompleteForm(false)} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={acting} className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50" data-testid="complete-submit">
                  {acting ? "Saving…" : "Complete & Credit FG Stock"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
      {run.status === "completed" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 flex items-start gap-3" data-testid="run-completed-banner">
          <CheckCircle size={24} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading text-base font-bold text-emerald-900">Run completed</div>
            <div className="text-xs text-emerald-800 mt-1">
              {run.actual_yield} kg yielded at ₹{run.fg_unit_cost}/kg cost. {run.fg_batch_no && `Batch: ${run.fg_batch_no}.`} {run.fg_expiry_date && `Expires ${run.fg_expiry_date}.`}
            </div>
          </div>
          <button
            onClick={() => navigate(`/inventory/production-wizard?clone=${run.id}`)}
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-emerald-300 text-emerald-800 text-xs font-semibold hover:bg-emerald-100 transition"
            data-testid="run-detail-clone-btn"
            title="Pre-fill the wizard with this run's recipe + batch size"
          >
            <Copy size={12} weight="bold" /> Clone as new run
          </button>
        </div>
      )}
      {run.status === "cancelled" && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-5 flex items-start gap-3" data-testid="run-cancelled-banner">
          <X size={24} weight="bold" className="text-rose-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-heading text-base font-bold text-rose-900">Run cancelled</div>
            <div className="text-xs text-rose-800 mt-1">No materials were consumed. You can create a new run with the same recipe.</div>
          </div>
        </div>
      )}

      {/* Materials breakdown */}
      {Object.keys(grouped).map((section) => (
        <div key={section} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.22em] text-slate-600 font-bold">
            {section}
          </div>
          <table className="w-full">
            <thead>
              <tr>
                {["Ingredient", "Brand", "Required", "Avail @plan", "Unit ₹", "Line ₹", "Shortage"].map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped[section].map((m, idx) => (
                <tr key={`${m.item_id}-${idx}`} className="border-t border-slate-100" data-testid={`material-row-${m.item_id}`}>
                  <td className="px-4 py-2 text-sm font-medium text-slate-900">{m.ingredient_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{m.brand}</td>
                  <td className="px-4 py-2 text-sm tabular-nums">{m.required} {m.uom}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">{m.available_at_plan} {m.uom}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">₹{Number(m.unit_cost_at_plan).toFixed(2)}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 tabular-nums">₹{m.line_cost}</td>
                  <td className={`px-4 py-2 text-xs tabular-nums font-semibold ${m.shortage_at_plan > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.shortage_at_plan > 0 ? `−${m.shortage_at_plan}` : "OK"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Consumed batches (after issue) */}
      {(run.consumed_batches || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-200 text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">
            Issued from these batches (FIFO/FEFO)
          </div>
          <table className="w-full">
            <thead>
              <tr>
                {["Ingredient", "Batch", "Qty", "Unit ₹", "Line ₹"].map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {run.consumed_batches.map((c, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-sm text-slate-900">{c.item_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">{c.batch_no}</td>
                  <td className="px-4 py-2 text-sm tabular-nums">{c.qty} {c.uom}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">₹{c.unit_cost}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 tabular-nums">₹{c.line_cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionPanel({ title, desc, ctaLabel, onClick, onCancel, disabled, warning, tone, testId }) {
  const toneMap = {
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    sky: "bg-sky-600 hover:bg-sky-700",
    amber: "bg-amber-600 hover:bg-amber-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testId}>
      <div className="font-heading text-base font-bold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
      {warning && (
        <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-start gap-2">
          <Warning size={14} weight="bold" className="text-amber-600 shrink-0 mt-0.5" /> {warning}
        </div>
      )}
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={onClick}
          disabled={disabled}
          className={`h-10 px-5 rounded-md text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2 ${toneMap[tone] || toneMap.emerald}`}
          data-testid={`${testId}-cta`}
        >
          {ctaLabel} <ArrowRight size={14} weight="bold" />
        </button>
        <button
          onClick={onCancel}
          disabled={disabled}
          className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-700"
          data-testid={`${testId}-cancel`}
        >
          Cancel run
        </button>
      </div>
    </div>
  );
}

function Tile({ label, value, tone = "slate" }) {
  const m = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold mt-1 ${m[tone]}`}>{value}</div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
